import { useState, useEffect } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getResponse, createResponse, type BodyType } from '@/api/responses'
import {
  updateScenario,
  type Scenario,
  type StaticConfig,
  type SequenceConfig,
  type ConditionalConfig,
  type StatefulCollectionConfig,
  type HttpActionMethod,
} from '@/api/scenarios'
import { ApiError } from '@/api/client'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  RuleBuilder,
  toApiRule,
  fromApiRule,
} from './RuleBuilder'
import {
  ResponseForm,
  defaultEntry,
  defaultResponse,
  type ResponseData,
  type RuleEntry,
} from './ConditionalScenarioForm'

const BODY_TYPES: BodyType[] = ['json', 'text', 'xml', 'binary_base64']

// ── Static edit form ──────────────────────────────────────────────────────

const staticSchema = z.object({
  scenarioName: z.string().min(1).max(100),
  priority: z.coerce.number().int().default(0),
  enabled: z.boolean().default(true),
  status: z.coerce.number().int().min(100).max(599).default(200),
  body_type: z.enum(['json', 'text', 'xml', 'binary_base64'] as const).default('json'),
  body: z.string().default(''),
  headersJson: z
    .string()
    .default('{}')
    .refine((s) => { try { JSON.parse(s); return true } catch { return false } }),
  delay_ms: z.coerce.number().int().min(0).max(30000).default(0),
})

type StaticFormValues = z.infer<typeof staticSchema>

interface StaticEditFormProps {
  scenario: Scenario
  projectId: string
  endpointId: string
  onSuccess: () => void
  onCancel: () => void
}

function StaticEditForm({ scenario, projectId, endpointId, onSuccess, onCancel }: StaticEditFormProps) {
  const [serverError, setServerError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const responseId = (scenario.config as StaticConfig).response_id

  const { data: existingResponse, isLoading: responseLoading } = useQuery({
    queryKey: ['response', responseId],
    queryFn: () => getResponse(responseId),
  })

  const { register, handleSubmit, control, formState: { errors, isDirty, dirtyFields, isSubmitting } } =
    useForm<StaticFormValues>({
      resolver: zodResolver(staticSchema),
      values: existingResponse
        ? {
            scenarioName: scenario.name,
            priority: scenario.priority,
            enabled: scenario.enabled,
            status: existingResponse.status,
            body_type: existingResponse.body_type,
            body: existingResponse.body ?? '',
            headersJson: JSON.stringify(existingResponse.headers ?? {}, null, 2),
            delay_ms: existingResponse.delay_ms,
          }
        : undefined,
    })

  const mutation = useMutation({
    mutationFn: async (data: StaticFormValues) => {
      const responseFieldsDirty = ['status', 'body_type', 'body', 'headersJson', 'delay_ms'].some(
        (f) => dirtyFields[f as keyof StaticFormValues],
      )

      let configUpdate: { config: StaticConfig } | Record<string, never> = {}

      if (responseFieldsDirty) {
        const headers = JSON.parse(data.headersJson) as Record<string, string>
        const newResponse = await createResponse(projectId, {
          status: data.status,
          body_type: data.body_type,
          body: data.body || undefined,
          headers,
          delay_ms: data.delay_ms,
        })
        configUpdate = { config: { response_id: newResponse.id } }
      }

      await updateScenario(scenario.id, {
        name: data.scenarioName,
        priority: data.priority,
        enabled: data.enabled,
        ...configUpdate,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['scenarios', endpointId] })
      await queryClient.invalidateQueries({ queryKey: ['response', responseId] })
      onSuccess()
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : t('scenarioForm.updateFailed'))
    },
  })

  if (responseLoading) {
    return <div className="py-8 text-center text-sm text-muted-foreground">{t('common.loading')}</div>
  }

  return (
    <form onSubmit={handleSubmit((d) => void mutation.mutateAsync(d))} className="space-y-5">
      {serverError && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{serverError}</p>
      )}

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('scenarioForm.scenarioSettings')}</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="edit-name">{t('scenarioForm.scenarioName')}</Label>
            <Input id="edit-name" {...register('scenarioName')} />
            {errors.scenarioName && <p className="text-sm text-destructive">{t('scenarioForm.nameRequired')}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-priority">{t('scenarioForm.priority')}</Label>
            <Input id="edit-priority" type="number" {...register('priority')} />
          </div>
          <div className="space-y-1.5">
            <Label className="block">{t('scenarioForm.enabled')}</Label>
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <Switch checked={field.value} onCheckedChange={field.onChange} className="mt-1" />
              )}
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('scenarioForm.responseSettings')}</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="edit-status">{t('scenarioForm.statusCode')}</Label>
            <Input id="edit-status" type="number" {...register('status')} />
            {errors.status && <p className="text-sm text-destructive">{errors.status.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>{t('scenarioForm.bodyType')}</Label>
            <Controller
              name="body_type"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BODY_TYPES.map((bt) => <SelectItem key={bt} value={bt}>{bt}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-delay">{t('scenarioForm.delayMs')}</Label>
            <Input id="edit-delay" type="number" {...register('delay_ms')} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="edit-body">{t('scenarioForm.body')}</Label>
          <Textarea id="edit-body" rows={6} className="font-mono text-sm" {...register('body')} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="edit-headers">{t('scenarioForm.headers')}</Label>
          <Textarea id="edit-headers" rows={3} className="font-mono text-sm" {...register('headersJson')} />
          {errors.headersJson && <p className="text-sm text-destructive">{t('scenarioForm.jsonFormatError')}</p>}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting || mutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || mutation.isPending || !isDirty}>
          {mutation.isPending ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </form>
  )
}

// ── Sequence edit form ────────────────────────────────────────────────────

type MatchOperator = 'exact' | '>=' | '<=' | '>' | '<'

const sequenceEditSchema = z.object({
  scenarioName: z.string().min(1).max(100),
  priority: z.coerce.number().int().default(0),
  enabled: z.boolean().default(true),
  scope: z.enum(['global', 'per_ip', 'per_header'] as const).default('global'),
  per_header_name: z.string().optional(),
  reset_after_seconds: z.union([z.coerce.number().int().positive(), z.literal('')]).optional(),
  steps: z.array(z.object({
    match_operator: z.enum(['exact', '>=', '<=', '>', '<'] as const).default('exact'),
    match_value: z.coerce.number().int().min(1),
    status: z.coerce.number().int().min(100).max(599).default(200),
    body_type: z.enum(['json', 'text', 'xml', 'binary_base64'] as const).default('json'),
    body: z.string().default(''),
    headersJson: z
      .string()
      .default('{}')
      .refine((s) => { try { JSON.parse(s); return true } catch { return false } }),
    delay_ms: z.coerce.number().int().min(0).max(30000).default(0),
    _responseId: z.string().optional(),
  })).min(1),
})

type SequenceEditFormValues = z.infer<typeof sequenceEditSchema>

function parseMatchCount(mc: number | string): { operator: MatchOperator; value: number } {
  if (typeof mc === 'number') return { operator: 'exact', value: mc }
  const match = /^(>=|<=|>|<)(\d+)$/.exec(String(mc))
  if (match) return { operator: match[1] as MatchOperator, value: Number(match[2]) }
  return { operator: 'exact', value: 1 }
}

function buildMatchCount(op: MatchOperator, val: number): number | string {
  return op === 'exact' ? val : `${op}${val}`
}

function buildScope(scope: string, headerName?: string): string {
  if (scope === 'per_header') return `per_header:${headerName ?? 'x-user-id'}`
  return scope
}

interface SequenceEditFormProps {
  scenario: Scenario
  projectId: string
  endpointId: string
  onSuccess: () => void
  onCancel: () => void
}

function SequenceEditForm({ scenario, projectId, endpointId, onSuccess, onCancel }: SequenceEditFormProps) {
  const [serverError, setServerError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const config = scenario.config as unknown as SequenceConfig

  const parsedScope = config.scope?.startsWith('per_header:')
    ? 'per_header'
    : (config.scope ?? 'global')

  const defaultSteps = config.steps.map((step) => {
    const { operator, value } = parseMatchCount(step.match_count)
    return {
      match_operator: operator,
      match_value: value,
      status: 200,
      body_type: 'json' as const,
      body: '',
      headersJson: '{}',
      delay_ms: 0,
      _responseId: step.response_id,
    }
  })

  const stepResponseIds = config.steps.map((s) => s.response_id)
  const stepResponseQueries = useQuery({
    queryKey: ['responses', ...stepResponseIds],
    queryFn: async () => {
      const results = await Promise.all(stepResponseIds.map((id) => getResponse(id)))
      return results
    },
    enabled: stepResponseIds.length > 0,
  })

  const stepsWithData = stepResponseQueries.data
    ? defaultSteps.map((step, i) => {
        const res = stepResponseQueries.data[i]
        return res
          ? {
              ...step,
              status: res.status,
              body_type: res.body_type,
              body: res.body ?? '',
              headersJson: JSON.stringify(res.headers ?? {}, null, 2),
              delay_ms: res.delay_ms,
            }
          : step
      })
    : undefined

  const { register, handleSubmit, control, watch, formState: { errors, isDirty, isSubmitting } } =
    useForm<SequenceEditFormValues>({
      resolver: zodResolver(sequenceEditSchema),
      values: stepsWithData
        ? {
            scenarioName: scenario.name,
            priority: scenario.priority,
            enabled: scenario.enabled,
            scope: parsedScope as 'global' | 'per_ip' | 'per_header',
            per_header_name: config.scope?.startsWith('per_header:') ? config.scope.split(':')[1] : '',
            reset_after_seconds: config.reset_after_seconds ?? '',
            steps: stepsWithData,
          }
        : undefined,
    })

  const { fields, append, remove, swap } = useFieldArray({ control, name: 'steps' })
  const scope = watch('scope')

  const mutation = useMutation({
    mutationFn: async (data: SequenceEditFormValues) => {
      const responses = await Promise.all(
        data.steps.map((step) => {
          const headers = JSON.parse(step.headersJson) as Record<string, string>
          return createResponse(projectId, {
            status: step.status,
            body_type: step.body_type,
            body: step.body || undefined,
            headers,
            delay_ms: step.delay_ms,
          })
        })
      )

      const steps = data.steps.map((step, i) => ({
        id: crypto.randomUUID(),
        match_count: buildMatchCount(step.match_operator as MatchOperator, step.match_value),
        response_id: responses[i].id,
      }))

      const scopeStr = buildScope(data.scope, data.per_header_name)
      const reset = data.reset_after_seconds !== '' && data.reset_after_seconds !== undefined
        ? Number(data.reset_after_seconds)
        : undefined

      await updateScenario(scenario.id, {
        name: data.scenarioName,
        priority: data.priority,
        enabled: data.enabled,
        config: { steps, scope: scopeStr, reset_after_seconds: reset },
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['scenarios', endpointId] })
      onSuccess()
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : t('scenarioForm.updateFailed'))
    },
  })

  if (stepResponseQueries.isLoading) {
    return <div className="py-8 text-center text-sm text-muted-foreground">{t('common.loading')}</div>
  }

  return (
    <form onSubmit={handleSubmit((d) => void mutation.mutateAsync(d))} className="space-y-5">
      {serverError && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{serverError}</p>
      )}

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('scenarioForm.scenarioSettings')}</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="seq-edit-name">{t('scenarioForm.scenarioName')}</Label>
            <Input id="seq-edit-name" {...register('scenarioName')} />
            {errors.scenarioName && <p className="text-sm text-destructive">{t('scenarioForm.nameRequired')}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="seq-edit-priority">{t('scenarioForm.priority')}</Label>
            <Input id="seq-edit-priority" type="number" {...register('priority')} />
          </div>
          <div className="space-y-1.5">
            <Label className="block">{t('scenarioForm.enabled')}</Label>
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <Switch checked={field.value} onCheckedChange={field.onChange} className="mt-1" />
              )}
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('scenarioForm.sequenceSettings')}</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t('scenarioForm.scope')}</Label>
            <Controller
              name="scope"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">global</SelectItem>
                    <SelectItem value="per_ip">per_ip</SelectItem>
                    <SelectItem value="per_header">per_header</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          {scope === 'per_header' && (
            <div className="space-y-1.5">
              <Label htmlFor="seq-header-name">{t('scenarioForm.headerName')}</Label>
              <Input id="seq-header-name" placeholder="x-user-id" {...register('per_header_name')} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="seq-reset">{t('scenarioForm.resetSeconds')}</Label>
            <Input id="seq-reset" type="number" min={1} placeholder={t('scenarioForm.resetSecondsPlaceholder')} {...register('reset_after_seconds')} />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          {t('scenarioForm.steps')} ({fields.length})
        </h4>
        <div className="space-y-3">
          {fields.map((field, i) => (
            <div key={field.id} className="rounded-md border bg-muted/20 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">{t('scenarioForm.step')} {i + 1}</span>
                <div className="flex gap-1">
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => swap(i, i - 1)} disabled={i === 0}><ChevronUp className="h-3 w-3" /></Button>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => swap(i, i + 1)} disabled={i === fields.length - 1}><ChevronDown className="h-3 w-3" /></Button>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => remove(i)} disabled={fields.length <= 1}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 items-end">
                <div className="space-y-1">
                  <Label className="text-xs">{t('scenarioForm.condition')}</Label>
                  <Controller
                    name={`steps.${i}.match_operator`}
                    control={control}
                    render={({ field: f }) => (
                      <Select value={f.value} onValueChange={f.onChange}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="exact">{t('scenarioForm.conditionExact')}</SelectItem>
                          <SelectItem value=">=">{t('scenarioForm.conditionGte')}</SelectItem>
                          <SelectItem value="<=">{t('scenarioForm.conditionLte')}</SelectItem>
                          <SelectItem value=">">&gt;</SelectItem>
                          <SelectItem value="<">&lt;</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('scenarioForm.requestCount')}</Label>
                  <Input className="h-9" type="number" min={1} {...register(`steps.${i}.match_value`)} />
                </div>
                <p className="text-xs text-muted-foreground pb-2">{t('scenarioForm.matchesSuffix')}</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">{t('scenarioForm.statusCode')}</Label>
                  <Input className="h-9" type="number" {...register(`steps.${i}.status`)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('scenarioForm.bodyType')}</Label>
                  <Controller
                    name={`steps.${i}.body_type`}
                    control={control}
                    render={({ field: f }) => (
                      <Select value={f.value} onValueChange={f.onChange}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {BODY_TYPES.map((bt) => <SelectItem key={bt} value={bt}>{bt}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('scenarioForm.delayMs')}</Label>
                  <Input className="h-9" type="number" {...register(`steps.${i}.delay_ms`)} />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('scenarioForm.body')}</Label>
                <Textarea rows={2} className={cn('font-mono text-sm')} {...register(`steps.${i}.body`)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('scenarioForm.headers')}</Label>
                <Textarea rows={2} className="font-mono text-sm" {...register(`steps.${i}.headersJson`)} />
              </div>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => append({ match_operator: 'exact', match_value: fields.length + 1, status: 200, body_type: 'json', body: '', headersJson: '{}', delay_ms: 0 })}
        >
          <Plus className="mr-2 h-4 w-4" />{t('scenarioForm.addStep')}
        </Button>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting || mutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || mutation.isPending || !isDirty}>
          {mutation.isPending ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </form>
  )
}

// ── Conditional edit form ─────────────────────────────────────────────────

const conditionalEditSchema = z.object({
  scenarioName: z.string().min(1).max(100),
  priority: z.coerce.number().int().default(0),
  enabled: z.boolean().default(true),
})

type ConditionalEditFormValues = z.infer<typeof conditionalEditSchema>

interface ConditionalEditFormProps {
  scenario: Scenario
  projectId: string
  endpointId: string
  onSuccess: () => void
  onCancel: () => void
}

function ConditionalEditForm({ scenario, projectId, endpointId, onSuccess, onCancel }: ConditionalEditFormProps) {
  const [serverError, setServerError] = useState<string | null>(null)
  const [initialized, setInitialized] = useState(false)
  const [ruleEntries, setRuleEntries] = useState<RuleEntry[]>([defaultEntry()])
  const [defaultResp, setDefaultResp] = useState<ResponseData | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const config = scenario.config as unknown as ConditionalConfig
  const ruleResponseIds = config.rules.map(r => r.then.response_id)
  const defaultRespId = config.default?.response_id
  const allIds = defaultRespId ? [...ruleResponseIds, defaultRespId] : ruleResponseIds

  const { data: responses, isLoading } = useQuery({
    queryKey: ['responses-cond-edit', scenario.id, ...allIds],
    queryFn: () => Promise.all(allIds.map(id => getResponse(id))),
    enabled: allIds.length > 0,
  })

  useEffect(() => {
    if (!responses || initialized) return
    setRuleEntries(
      config.rules.map((rule, i) => {
        const res = responses[i]
        return {
          id: crypto.randomUUID(),
          ruleNode: fromApiRule(rule.if),
          response: {
            status: res.status,
            body_type: res.body_type,
            body: res.body ?? '',
            headersJson: JSON.stringify(res.headers ?? {}, null, 2),
            delay_ms: res.delay_ms,
          },
        }
      })
    )
    if (defaultRespId) {
      const dr = responses[config.rules.length]
      setDefaultResp({
        status: dr.status,
        body_type: dr.body_type,
        body: dr.body ?? '',
        headersJson: JSON.stringify(dr.headers ?? {}, null, 2),
        delay_ms: dr.delay_ms,
      })
    }
    setInitialized(true)
  }, [responses, initialized, config.rules, config.default, defaultRespId])

  const { register, handleSubmit, control, formState: { errors, isSubmitting } } =
    useForm<ConditionalEditFormValues>({
      resolver: zodResolver(conditionalEditSchema),
      defaultValues: {
        scenarioName: scenario.name,
        priority: scenario.priority,
        enabled: scenario.enabled,
      },
    })

  const updateEntry = (id: string, partial: Partial<RuleEntry>) =>
    setRuleEntries(prev => prev.map(e => e.id === id ? { ...e, ...partial } : e))
  const removeEntry = (id: string) =>
    setRuleEntries(prev => prev.filter(e => e.id !== id))
  const moveEntry = (id: string, dir: 'up' | 'down') => {
    setRuleEntries(prev => {
      const idx = prev.findIndex(e => e.id === id)
      if (idx < 0) return prev
      const next = [...prev]
      const swap = dir === 'up' ? idx - 1 : idx + 1
      if (swap < 0 || swap >= next.length) return prev
      ;[next[idx], next[swap]] = [next[swap], next[idx]]
      return next
    })
  }

  const parseH = (json: string) => JSON.parse(json) as Record<string, string>

  const mutation = useMutation({
    mutationFn: async (data: ConditionalEditFormValues) => {
      if (ruleEntries.length === 0) throw new Error(t('scenarioForm.atLeastOneAction'))
      for (const e of ruleEntries) {
        try { parseH(e.response.headersJson) } catch { throw new Error(t('scenarioForm.headersJsonError')) }
      }
      if (defaultResp) {
        try { parseH(defaultResp.headersJson) } catch { throw new Error(t('scenarioForm.headersJsonError')) }
      }
      const ruleResponses = await Promise.all(
        ruleEntries.map(e => createResponse(projectId, {
          status: e.response.status,
          body_type: e.response.body_type,
          body: e.response.body || undefined,
          headers: parseH(e.response.headersJson),
          delay_ms: e.response.delay_ms,
        }))
      )
      let defRespId: string | undefined
      if (defaultResp) {
        const dr = await createResponse(projectId, {
          status: defaultResp.status,
          body_type: defaultResp.body_type,
          body: defaultResp.body || undefined,
          headers: parseH(defaultResp.headersJson),
          delay_ms: defaultResp.delay_ms,
        })
        defRespId = dr.id
      }
      await updateScenario(scenario.id, {
        name: data.scenarioName,
        priority: data.priority,
        enabled: data.enabled,
        config: {
          rules: ruleEntries.map((e, i) => ({
            if: toApiRule(e.ruleNode),
            then: { response_id: ruleResponses[i].id },
          })),
          ...(defRespId ? { default: { response_id: defRespId } } : {}),
        },
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['scenarios', endpointId] })
      onSuccess()
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : t('scenarioForm.updateFailed'))
    },
  })

  if (isLoading) {
    return <div className="py-8 text-center text-sm text-muted-foreground">{t('common.loading')}</div>
  }

  return (
    <form onSubmit={handleSubmit((d) => void mutation.mutateAsync(d))} className="space-y-5">
      {serverError && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{serverError}</p>
      )}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('scenarioForm.scenarioSettings')}</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="cond-edit-name">{t('scenarioForm.scenarioName')}</Label>
            <Input id="cond-edit-name" {...register('scenarioName')} />
            {errors.scenarioName && <p className="text-sm text-destructive">{t('scenarioForm.nameRequired')}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cond-edit-priority">{t('scenarioForm.priority')}</Label>
            <Input id="cond-edit-priority" type="number" {...register('priority')} />
          </div>
          <div className="space-y-1.5">
            <Label className="block">{t('scenarioForm.enabled')}</Label>
            <Controller name="enabled" control={control} render={({ field }) => (
              <Switch checked={field.value} onCheckedChange={field.onChange} className="mt-1" />
            )} />
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('scenarioForm.ruleList')} ({ruleEntries.length})
        </h4>
        <div className="space-y-4">
          {ruleEntries.map((entry, idx) => (
            <div key={entry.id} className="rounded-md border bg-muted/10 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{t('scenarioForm.rule')} {idx + 1}</span>
                <div className="ml-auto flex gap-1">
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveEntry(entry.id, 'up')} disabled={idx === 0}><ChevronUp className="h-3.5 w-3.5" /></Button>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveEntry(entry.id, 'down')} disabled={idx === ruleEntries.length - 1}><ChevronDown className="h-3.5 w-3.5" /></Button>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => removeEntry(entry.id)} disabled={ruleEntries.length <= 1}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground">{t('scenarioForm.ifCondition')}</p>
                <RuleBuilder node={entry.ruleNode} onChange={(ruleNode) => updateEntry(entry.id, { ruleNode })} />
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground">{t('scenarioForm.thenResponse')}</p>
                <ResponseForm value={entry.response} onChange={(response) => updateEntry(entry.id, { response })} />
              </div>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => setRuleEntries(prev => [...prev, defaultEntry()])}>
          <Plus className="mr-2 h-4 w-4" />{t('scenarioForm.addRule')}
        </Button>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('scenarioForm.defaultResponse')}</h4>
          {defaultResp === null ? (
            <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setDefaultResp(defaultResponse())}>
              <Plus className="mr-1 h-3 w-3" />{t('scenarioForm.setDefault')}
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-destructive" onClick={() => setDefaultResp(null)}>
              {t('scenarioForm.removeDefault')}
            </Button>
          )}
        </div>
        {defaultResp !== null ? (
          <ResponseForm value={defaultResp} onChange={setDefaultResp} />
        ) : (
          <p className="text-xs text-muted-foreground">{t('scenarioForm.defaultNoMatch')}</p>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting || mutation.isPending}>{t('common.cancel')}</Button>
        <Button type="submit" disabled={isSubmitting || mutation.isPending}>
          {mutation.isPending ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </form>
  )
}

// ── Stateful Collection edit form ─────────────────────────────────────────

const HTTP_METHODS_SF: HttpActionMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

const sfActionSchema = z.object({
  enabled:          z.boolean().default(false),
  method:           z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const).default('GET'),
  path_match:       z.string().default(''),
  response_status:  z.coerce.number().int().min(100).max(599).default(200),
  not_found_status: z.coerce.number().int().min(100).max(599).default(404),
})

const sfSchema = z.object({
  scenarioName:      z.string().min(1).max(100),
  priority:          z.coerce.number().int().default(0),
  enabled:           z.boolean().default(true),
  state_key:         z.string().min(1).max(100).regex(/^[a-z0-9_-]+$/),
  id_field:          z.string().min(1).default('id'),
  id_strategy_type:  z.enum(['uuid', 'sequential', 'from_body'] as const).default('uuid'),
  id_strategy_field: z.string().default(''),
  max_items:         z.coerce.number().int().positive().default(100),
  ttl_seconds:       z.coerce.number().int().positive().default(86400),
  actions: z.object({
    create: sfActionSchema,
    list:   sfActionSchema,
    get:    sfActionSchema,
    update: sfActionSchema,
    delete: sfActionSchema,
  }),
})

type SfFormValues = z.infer<typeof sfSchema>
type SfActionName = 'create' | 'list' | 'get' | 'update' | 'delete'

function SfActionCard({ name, control, register, watch, errors }: {
  name: SfActionName
  control: ReturnType<typeof useForm<SfFormValues>>['control']
  register: ReturnType<typeof useForm<SfFormValues>>['register']
  watch: ReturnType<typeof useForm<SfFormValues>>['watch']
  errors: ReturnType<typeof useForm<SfFormValues>>['formState']['errors']
}) {
  const enabled = watch(`actions.${name}.enabled`)
  const actionErrors = errors.actions?.[name]
  const { t } = useTranslation()
  const label = `${name.charAt(0).toUpperCase() + name.slice(1)} (${t(`endpoint.actionLabels.${name}`)})`
  return (
    <div className={cn('rounded-md border p-3 space-y-3', !enabled && 'opacity-60')}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <Controller name={`actions.${name}.enabled`} control={control} render={({ field }) => (
          <Switch checked={field.value} onCheckedChange={field.onChange} />
        )} />
      </div>
      {enabled && (
        <div className="space-y-2">
          <div className="flex gap-2 items-start">
            <div className="w-[110px] shrink-0 space-y-1">
              <Label className="text-xs">Method</Label>
              <Controller name={`actions.${name}.method`} control={control} render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>{HTTP_METHODS_SF.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              )} />
            </div>
            <div className="flex-1 space-y-1">
              <Label className="text-xs">{t('scenarioForm.pathPattern')}</Label>
              <Input className="h-8 font-mono text-sm" placeholder="/items/:id" {...register(`actions.${name}.path_match`)} />
              {actionErrors?.path_match && <p className="text-xs text-destructive">{actionErrors.path_match.message}</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">{t('scenarioForm.successStatus')}</Label>
              <Input className="h-8" type="number" {...register(`actions.${name}.response_status`)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('scenarioForm.notFoundStatus')}</Label>
              <Input className="h-8" type="number" {...register(`actions.${name}.not_found_status`)} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function parseIdStrategy(s?: string): { type: 'uuid' | 'sequential' | 'from_body'; field: string } {
  if (!s || s === 'uuid') return { type: 'uuid', field: '' }
  if (s === 'sequential') return { type: 'sequential', field: '' }
  if (s.startsWith('from_body:')) return { type: 'from_body', field: s.slice(10) }
  return { type: 'uuid', field: '' }
}

interface StatefulEditFormProps {
  scenario: Scenario
  endpointId: string
  onSuccess: () => void
  onCancel: () => void
}

function StatefulEditForm({ scenario, endpointId, onSuccess, onCancel }: StatefulEditFormProps) {
  const [serverError, setServerError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const cfg = scenario.config as unknown as StatefulCollectionConfig
  const { type: idStrategyType, field: idStrategyField } = parseIdStrategy(cfg.id_strategy)

  const sfActionDefault = (name: SfActionName) => {
    const a = cfg.actions[name]
    return a
      ? { enabled: true, method: a.method, path_match: a.path_match, response_status: a.response_status ?? 200, not_found_status: a.not_found_status ?? 404 }
      : { enabled: false, method: 'GET' as HttpActionMethod, path_match: '', response_status: 200, not_found_status: 404 }
  }

  const { register, handleSubmit, control, watch, formState: { errors, isDirty, isSubmitting } } =
    useForm<SfFormValues>({
      resolver: zodResolver(sfSchema),
      defaultValues: {
        scenarioName: scenario.name,
        priority: scenario.priority,
        enabled: scenario.enabled,
        state_key: cfg.state_key,
        id_field: cfg.id_field ?? 'id',
        id_strategy_type: idStrategyType,
        id_strategy_field: idStrategyField,
        max_items: cfg.max_items ?? 100,
        ttl_seconds: cfg.ttl_seconds ?? 86400,
        actions: {
          create: sfActionDefault('create'),
          list:   sfActionDefault('list'),
          get:    sfActionDefault('get'),
          update: sfActionDefault('update'),
          delete: sfActionDefault('delete'),
        },
      },
    })

  const idStrategyType_ = watch('id_strategy_type')

  const mutation = useMutation({
    mutationFn: async (data: SfFormValues) => {
      const actionNames: SfActionName[] = ['create', 'list', 'get', 'update', 'delete']
      const actions: Record<string, { method: HttpActionMethod; path_match: string; response_status: number; not_found_status: number }> = {}
      for (const name of actionNames) {
        const a = data.actions[name]
        if (!a.enabled) continue
        if (!a.path_match.trim()) throw new Error(t('scenarioForm.actionPathRequired', { action: name }))
        actions[name] = { method: a.method, path_match: a.path_match.trim(), response_status: a.response_status, not_found_status: a.not_found_status }
      }
      if (Object.keys(actions).length === 0) throw new Error(t('scenarioForm.atLeastOneAction'))
      const id_strategy = data.id_strategy_type === 'from_body'
        ? `from_body:${data.id_strategy_field || 'id'}`
        : data.id_strategy_type
      await updateScenario(scenario.id, {
        name: data.scenarioName,
        priority: data.priority,
        enabled: data.enabled,
        config: { state_key: data.state_key, id_field: data.id_field, id_strategy, actions, max_items: data.max_items, ttl_seconds: data.ttl_seconds },
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['scenarios', endpointId] })
      onSuccess()
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : t('scenarioForm.updateFailed'))
    },
  })

  return (
    <form onSubmit={handleSubmit((d) => void mutation.mutateAsync(d))} className="space-y-5">
      {serverError && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{serverError}</p>}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('scenarioForm.scenarioSettings')}</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="sf-edit-name">{t('scenarioForm.scenarioName')}</Label>
            <Input id="sf-edit-name" {...register('scenarioName')} />
            {errors.scenarioName && <p className="text-sm text-destructive">{t('scenarioForm.nameRequired')}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sf-edit-priority">{t('scenarioForm.priority')}</Label>
            <Input id="sf-edit-priority" type="number" {...register('priority')} />
          </div>
          <div className="space-y-1.5">
            <Label className="block">{t('scenarioForm.enabled')}</Label>
            <Controller name="enabled" control={control} render={({ field }) => (
              <Switch checked={field.value} onCheckedChange={field.onChange} className="mt-1" />
            )} />
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('scenarioForm.collectionSettings')}</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="sf-state_key">{t('scenarioForm.stateKey')}</Label>
            <Input id="sf-state_key" className="font-mono" {...register('state_key')} />
            {errors.state_key && <p className="text-sm text-destructive">{t('scenarioForm.stateKeyRequired')}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sf-id_field">{t('scenarioForm.idField')}</Label>
            <Input id="sf-id_field" className="font-mono" {...register('id_field')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('scenarioForm.idStrategy')}</Label>
            <Controller name="id_strategy_type" control={control} render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="uuid">{t('scenarioForm.idStrategyUuid')}</SelectItem>
                  <SelectItem value="sequential">{t('scenarioForm.idStrategySequential')}</SelectItem>
                  <SelectItem value="from_body">{t('scenarioForm.idStrategyFromBody')}</SelectItem>
                </SelectContent>
              </Select>
            )} />
          </div>
          {idStrategyType_ === 'from_body' && (
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="sf-id_strategy_field">{t('scenarioForm.idStrategyField')}</Label>
              <Input id="sf-id_strategy_field" className="font-mono" {...register('id_strategy_field')} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="sf-max_items">{t('scenarioForm.maxItems')}</Label>
            <Input id="sf-max_items" type="number" min={1} {...register('max_items')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sf-ttl_seconds">{t('scenarioForm.ttlSeconds')}</Label>
            <Input id="sf-ttl_seconds" type="number" min={1} {...register('ttl_seconds')} />
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('scenarioForm.actionSettings')}</h4>
        <div className="space-y-2">
          {(['create', 'list', 'get', 'update', 'delete'] as SfActionName[]).map(name => (
            <SfActionCard key={name} name={name} control={control} register={register} watch={watch} errors={errors} />
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting || mutation.isPending}>{t('common.cancel')}</Button>
        <Button type="submit" disabled={isSubmitting || mutation.isPending || !isDirty}>
          {mutation.isPending ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </form>
  )
}

// ── Dialog wrapper ────────────────────────────────────────────────────────

interface EditScenarioDialogProps {
  scenario: Scenario
  projectId: string
  endpointId: string
}

export function EditScenarioDialog({ scenario, projectId, endpointId }: EditScenarioDialogProps) {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()

  const title =
    scenario.type === 'static' ? t('scenarioForm.editStaticTitle') :
    scenario.type === 'sequence' ? t('scenarioForm.editSequenceTitle') :
    scenario.type === 'conditional' ? t('scenarioForm.editConditionalTitle') :
    scenario.type === 'stateful_collection' ? t('scenarioForm.editStatefulTitle') :
    t('scenarioForm.editTitle')

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {scenario.type === 'static' && (
          <StaticEditForm
            scenario={scenario}
            projectId={projectId}
            endpointId={endpointId}
            onSuccess={() => setOpen(false)}
            onCancel={() => setOpen(false)}
          />
        )}
        {scenario.type === 'sequence' && (
          <SequenceEditForm
            scenario={scenario}
            projectId={projectId}
            endpointId={endpointId}
            onSuccess={() => setOpen(false)}
            onCancel={() => setOpen(false)}
          />
        )}
        {scenario.type === 'conditional' && (
          <ConditionalEditForm
            scenario={scenario}
            projectId={projectId}
            endpointId={endpointId}
            onSuccess={() => setOpen(false)}
            onCancel={() => setOpen(false)}
          />
        )}
        {scenario.type === 'stateful_collection' && (
          <StatefulEditForm
            scenario={scenario}
            endpointId={endpointId}
            onSuccess={() => setOpen(false)}
            onCancel={() => setOpen(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
